import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'

import CoreStoreModule from '@revas/kadra-library/src/revas/vue/vuex/CoreStoreModule'
import AppStoreModule from '@revas/kadra-library/src/core/app/vue/vuex/AppStoreModule'
import DesktopsStoreModuleContainer from './DesktopsStoreModuleContainer'

import { Client as RevasClient } from '@revas/ts-api'
import { AxiosInstance } from 'axios'
import { safeAxiosPost } from '../../utils/safe-post'
import VueI18n from 'vue-i18n'

import StoreActionLock from '@revas/kadra-library/src/core/app/vue/vuex/StoreActionLock'
import AppError from '@revas/kadra-library/src/core/app/vue/vuex/AppError'
import { App, Package } from '@/models'

export interface DesktopsStore {
  isAppInstalled: boolean;
  isAppUpdated: boolean;
  asyncCreateApp (payload: { package: Package }): Promise<void>;
  asyncUpdateApp (payload: { app: App }): Promise<void>;
  isAppDeleted: boolean;
  asyncDeleteApp (payload: { appId: string }): Promise<void>;
  apps: App[] | null;
  asyncListApps(): Promise<void>;
  packages: Package[] | null;
  asyncListPackages(): Promise<void>;
}

@Module({
  name: 'desktops',
  stateFactory: true,
  namespaced: true
})
export default class DesktopsStoreModule extends VuexModule implements DesktopsStore {
  private _coreStoreModule: CoreStoreModule | null = null
  private _appStoreModule: AppStoreModule | null = null
  private _revasClient: RevasClient | null = null
  private _axios: AxiosInstance | null = null
  private _i18n: VueI18n | null = null

  @Mutation
  inject (container: DesktopsStoreModuleContainer) {
    this._coreStoreModule = container.coreStoreModule
    this._appStoreModule = container.appStoreModule
    this._revasClient = container.revasClient
    this._axios = container.axios
    this._i18n = container.i18n
  }

  //
  //
  // Save App

  private _isAppInstalled = false
  public get isAppInstalled (): boolean {
    return this._isAppInstalled
  }

  private _isAppUpdated = false
  public get isAppUpdated (): boolean {
    return this._isAppUpdated
  }

  @Mutation
  private installedAppSuccess (): void {
    this._isAppInstalled = true
  }

  @Mutation
  private updatedAppSuccess (): void {
    this._isAppUpdated = true
    // aggiorno lista su base dell'utente, nuova posizione e modifico array delle app
  }

  @Action({ rawError: true })
  public async asyncCreateApp (payload: { package: Package }): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null || this._i18n === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }
    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [res, err] = await safeAxiosPost<any>(this._axios, '/desktops/desktops.CreateApp/', {
      app: {
        packageName: payload.package.name
      }
    })
    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }
    this.installedAppSuccess()
    this._appStoreModule.asyncUnlock(lock)
  }

  @Action({ rawError: true })
  public async asyncUpdateApp (payload: { app: App }): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null || this._i18n === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }
    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [res, err] = await safeAxiosPost<any>(this._axios, '/desktops/desktops.UpdateApp/', {
      app: payload.app,
      appId: payload.app.id
    })
    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }
    this.updatedAppSuccess()
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Delete App
  private _isAppDeleted = false
  public get isAppDeleted (): boolean {
    return this._isAppDeleted
  }

  @Mutation
  private deleteAppSuccess () {
    this._isAppDeleted = true
  }

  @Action({ rawError: true })
  public async asyncDeleteApp (payload: { appId: string }) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [res, err] = await safeAxiosPost<any>(this._axios, '/desktops/desktops.DeleteApp/', {
      appId: payload.appId
    })

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.deleteAppSuccess()
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // List Apps
  private _apps: App[] | null = null

  public get apps (): App[] | null {
    return this._apps
  }

  @Mutation
  private listAppsSuccess (payload: { apps: App[] }): void {
    this._apps = payload.apps
  }

  @Action({ rawError: true })
  async asyncListApps (): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [res, err] = await safeAxiosPost<any>(this._axios, '/desktops/desktops.ListApps/', {
      pageSize: 0,
      pageToken: '',
      search: ''
    })

    const lowestToHighest = res.apps.sort((a: App, b: App) => a.positionY - b.positionY).map((a: App) => {
      return {
        name: a.packageName,
        position: a.positionY
      }
    })
    console.log(JSON.stringify(lowestToHighest, null, '  '))

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.listAppsSuccess({
      apps: res.apps
    })

    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // List Packages
  private _packages: Package[] | null = null

  public get packages (): Package[] | null {
    return this._packages
  }

  @Mutation
  private listPackagesSuccess (payload: { packages: Package[] }): void {
    this._packages = payload.packages
  }

  @Action({ rawError: true })
  async asyncListPackages (): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [res, err] = await safeAxiosPost<any>(this._axios, '/desktops/desktops.ListPackages/', {
      pageSize: 0,
      pageToken: '',
      search: ''
    })

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.listPackagesSuccess({
      packages: res.packages
    })

    this._appStoreModule.asyncUnlock(lock)
  }
}
